import { useEffect, useState, useCallback } from 'react';
import { getDataStore, initializeDataStore, setAuthenticatedMode } from '../services/datastore';
import { migrateToSupabase, hasMigrated } from '../services/datastore/migration';
import type { AppUser, AuthMode, OfflineUser } from '../services/datastore/types';
import type { OAuthProvider } from '../services/auth';
import {
  getSession,
  getProviderFromSession,
  signInWithGoogle as authSignInWithGoogle,
  signInWithApple as authSignInWithApple,
  linkGoogleAccount as authLinkGoogleAccount,
  linkAppleAccount as authLinkAppleAccount,
  signOut as authSignOut,
  onAuthStateChange,
} from '../services/auth';

const OFFLINE_USER_KEY = 'singlist-offline-user';

export type AccountType = 'offline' | 'linked';

interface AuthState {
  user: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  mode: AuthMode;
  error: Error | null;
  /** Whether the account is offline-only or linked to OAuth */
  accountType: AccountType;
  /** The OAuth provider if linked (google/apple) */
  provider: OAuthProvider | null;
}

interface UseAuthReturn extends AuthState {
  /** Start using the app (creates offline user if needed) */
  startOffline: () => Promise<{ error: Error | null }>;
  /** Sign out and clear local data */
  signOut: () => Promise<{ error: Error | null }>;
  /** Sign in with Google OAuth */
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  /** Sign in with Apple OAuth */
  signInWithApple: () => Promise<{ error: Error | null }>;
  /** Link current offline account with Google */
  linkGoogleAccount: () => Promise<{ error: Error | null }>;
  /** Link current offline account with Apple */
  linkAppleAccount: () => Promise<{ error: Error | null }>;
}

/**
 * Authentication hook for offline-first mode with OAuth support.
 *
 * Key design decisions:
 * - No automatic user creation on page load
 * - User must explicitly click "Get Started" to create offline session
 * - All data stored locally in IndexedDB
 * - Can be upgraded to linked account via OAuth (Google/Apple)
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [mode, setMode] = useState<AuthMode>('offline');
  const [accountType, setAccountType] = useState<AccountType>('offline');
  const [provider, setProvider] = useState<OAuthProvider | null>(null);

  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      try {
        // Initialize the datastore
        await initializeDataStore();

        // Check for existing Supabase session first
        const { data: sessionData } = await getSession();

        if (sessionData?.session) {
          // User has an active Supabase session (linked account)
          const supabaseUser = sessionData.session.user;
          const detectedProvider = getProviderFromSession(sessionData.session);

          // Set authenticated mode for datastore selection
          setAuthenticatedMode(true);

          if (mounted) {
            setUser({
              id: supabaseUser.id,
              mode: 'authenticated',
              email: supabaseUser.email,
              displayName: supabaseUser.user_metadata?.full_name || supabaseUser.email,
              provider: detectedProvider ?? undefined,
            });
            setMode('authenticated');
            setAccountType('linked');
            setProvider(detectedProvider);
          }
        } else {
          // Check for existing offline user
          const store = getDataStore();
          const result = await store.auth.getCurrentUser();

          if (result.error) {
            throw new Error(result.error.message);
          }

          if (mounted && result.data) {
            setUser(result.data);
            setMode(result.data.mode);
            setAccountType(result.data.mode === 'authenticated' ? 'linked' : 'offline');
            if (result.data.mode === 'authenticated') {
              setProvider(result.data.provider ?? null);
            }
          }
        }
      } catch (err) {
        console.error('[useAuth] initAuth error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Authentication failed'));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    initAuth();

    // Listen for auth state changes (e.g., after OAuth callback)
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {

      if (event === 'SIGNED_IN' && session) {
        const supabaseUser = (session as { user: { id: string; email?: string; user_metadata?: { full_name?: string } } }).user;
        const detectedProvider = getProviderFromSession(session as { user?: { app_metadata?: { provider?: string; providers?: string[] } } });

        // Migrate offline data to Supabase if not already done
        if (!hasMigrated(supabaseUser.id)) {
          const offlineUserData = localStorage.getItem(OFFLINE_USER_KEY);
          if (offlineUserData) {
            try {
              const offlineUser = JSON.parse(offlineUserData) as OfflineUser;
              await migrateToSupabase(offlineUser.id, supabaseUser.id);
            } catch (err) {
              console.error('[useAuth] Migration failed:', err);
            }
          }
        }

        // Set authenticated mode for datastore selection
        setAuthenticatedMode(true);

        setUser({
          id: supabaseUser.id,
          mode: 'authenticated',
          email: supabaseUser.email,
          displayName: supabaseUser.user_metadata?.full_name || supabaseUser.email,
          provider: detectedProvider ?? undefined,
        });
        setMode('authenticated');
        setAccountType('linked');
        setProvider(detectedProvider);
      } else if (event === 'SIGNED_OUT') {
        // Reset to offline state - user data remains in IndexedDB
        setAuthenticatedMode(false);
        setAccountType('offline');
        setProvider(null);
        setMode('offline');
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Start using the app in offline mode.
   * Creates a local user if one doesn't exist.
   */
  const startOffline = useCallback(async (): Promise<{ error: Error | null }> => {
    try {
      const store = getDataStore();
      const result = await store.auth.getOrCreateUser();

      if (result.error) {
        throw new Error(result.error.message);
      }

      setUser(result.data);
      setMode('offline');
      return { error: null };
    } catch (err) {
      console.error('[useAuth] startOffline error:', err);
      const error = err instanceof Error ? err : new Error('Failed to start offline mode');
      setError(error);
      return { error };
    }
  }, []);

  /**
   * Sign out and clear local user data.
   */
  const signOut = useCallback(async (): Promise<{ error: Error | null }> => {
    try {
      // Sign out from Supabase if we have a session
      if (accountType === 'linked') {
        const { error: supabaseError } = await authSignOut();
        if (supabaseError) {
          console.warn('[useAuth] Supabase signOut error:', supabaseError);
        }
      }

      // Clear local datastore
      const store = getDataStore();
      const result = await store.auth.clear();

      if (result.error) {
        throw new Error(result.error.message);
      }

      setUser(null);
      setAccountType('offline');
      setProvider(null);
      return { error: null };
    } catch (err) {
      console.error('[useAuth] signOut error:', err);
      const error = err instanceof Error ? err : new Error('Failed to sign out');
      setError(error);
      return { error };
    }
  }, [accountType]);

  /**
   * Sign in with Google OAuth.
   * Initiates OAuth flow - redirects to Google.
   */
  const signInWithGoogle = useCallback(async (): Promise<{ error: Error | null }> => {
    return authSignInWithGoogle();
  }, []);

  /**
   * Sign in with Apple OAuth.
   * Initiates OAuth flow - redirects to Apple.
   */
  const signInWithApple = useCallback(async (): Promise<{ error: Error | null }> => {
    return authSignInWithApple();
  }, []);

  /**
   * Link current offline account with Google.
   * Upgrades the account to a linked account.
   */
  const linkGoogleAccount = useCallback(async (): Promise<{ error: Error | null }> => {
    return authLinkGoogleAccount();
  }, []);

  /**
   * Link current offline account with Apple.
   * Upgrades the account to a linked account.
   */
  const linkAppleAccount = useCallback(async (): Promise<{ error: Error | null }> => {
    return authLinkAppleAccount();
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    mode,
    error,
    accountType,
    provider,
    startOffline,
    signOut,
    signInWithGoogle,
    signInWithApple,
    linkGoogleAccount,
    linkAppleAccount,
  };
}
